import { decodePushMessage } from "../../utils/DecodePushMessage";
import matchSharedSecret from "./MatchSharedSecret";
import fetchMatchingTransactions from "./FetchMatchingTransactions";
import dispatchToChat from "./DispatchToChat";
import sha256 from "crypto-js/sha256";
import bs58 from "bs58";
import { MESSAGES_TYPES } from '../../Config';
import { deserializeMediaPayload } from "../../utils/ImageManager";

const recentlyProcessedSlots = new Set();
const MAX_TRACKED_SLOTS = 100;
const blockCache = new Map();

/**
 * MessageProcessor handles processing of incoming encrypted push blobs.
 * - Ensures the message is intended for the local ratcheted keys.
 * - Avoids reprocessing the same slot/key pair.
 * - Reuses parsed block data via a capped in-memory cache.
 */
const MessageProcessor = {
  async process({
    blobData,
    receiverIdentityViewPublicKey,
    receiverViewPublicKey,
    receiverViewPrivateKey,
    sessionKyberKeys,
    password,
    userId,
    salt,
  }) {
    try {
      const rawBytes =
        blobData instanceof Uint8Array ? blobData : new Uint8Array(blobData);

      // Decode the incoming push message
      const { slot, ephemeralPublicKey, sharedSecretHint } = decodePushMessage(rawBytes);

      const slot_handle = sha256(slot + bs58.encode(receiverViewPublicKey)).toString();

      // Check if already processed
      if (recentlyProcessedSlots.has(slot_handle)) {
        return;
      }

      // Add slot handle to tracking
      recentlyProcessedSlots.add(slot_handle);
      if (recentlyProcessedSlots.size > MAX_TRACKED_SLOTS) {
        const oldest = recentlyProcessedSlots.values().next().value;
        recentlyProcessedSlots.delete(oldest);
      }

      // Add raw slot to block cache
      if (!blockCache.has(slot)) {
        blockCache.set(slot, null); // placeholder for now
        if (blockCache.size > MAX_TRACKED_SLOTS) {
          const oldestSlot = blockCache.keys().next().value;
          blockCache.delete(oldestSlot);
        }
      }

      // Check if this message is meant for us
      const match = matchSharedSecret({
        ephemeralPublicKey,
        sharedSecretHint,
        receiverPrivateKey: receiverViewPrivateKey,
      });

      if (!match) {
        return;
      }

      console.log("[MessageProcessor] Message matches this client.");

      // Fetch transaction(s) for the slot
      const messages = await fetchMatchingTransactions({
        slot,
        viewPublicKey: receiverViewPublicKey,
        viewPrivateKey: receiverViewPrivateKey,
        sessionKyberKeys,
        blockCache,
      });

      if (!messages || !Array.isArray(messages) || messages.length === 0) {
        console.log("[MessageProcessor] No matching transactions found at slot:", slot);
        return;
      }

      for (const message of messages) {
        if (message.isParsed) {

          // non unlink protocol message type, parse manually
          if (message.messageType === MESSAGES_TYPES.MESSAGE_TYPE_MEDIA) {

            message.parsed = deserializeMediaPayload(message.parsed);
          }

          dispatchToChat({ 
            message,
            receiverIdentityViewPublicKey,
            userId, 
            password, 
            salt, 
          });
        }
      }

      return true;
    } catch (err) {
      console.log("[MessageProcessor] Failed to process message:", err);
    }
  },
};

export default MessageProcessor;